import React, { useEffect, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  DatePicker,
  Empty,
  Flex,
  Form,
  Input,
  Spin,
  Table,
  Tag,
  Tooltip,
  Typography,
  theme,
} from '@duxton-web/ui';
import { colorBaseNeutral75 } from '@duxton-web/design-tokens/themes/light';
import {
  DocumentOutlined,
  SearchUniversal,
  TicketOutlined,
  UserOutlined,
} from '@duxton-web/icons';
import { useDispatch, useSelector } from 'react-redux';
import _ from 'lodash';
import { searchSmartCommsJourney } from 'omega-rtc/actions/debugging-tools';
import DebuggingToolsDrawer, {
  JourneyRecord,
  formatDate,
} from './DebuggingToolsDrawer';
import { getCampaignUrl } from './debuggingToolsConstants';

const { Title } = Typography;
const { RangePicker } = DatePicker;

const RESULTS_PAGE_SIZE = 25;
const DELIVERY_SUCCESS_STATUSES = ['sent', 'delivered', 'success'];

const ERROR_TYPE_FILTER_TEXT = {
  FAILED_RF: 'RF failed',
  DROPPED_FREQ_CAP: 'XCO freq cap',
};

type ErrorType = {
  label: string;
  color: string;
};

const missingValue = <Typography.Text type="secondary">-</Typography.Text>;

const getContentUrl = (templateID: string): string =>
  `${window.Grab.config.HEDWIG_DASH_URI}/templates/create/push/?id=${templateID}`;

const getActualSentTime = (record: JourneyRecord): string | null => {
  if (record.hedwigStreamtime != null) {
    return record.hedwigStreamtime;
  }

  if (record.xcoStatus === 'SELECTED_FOR_DISPATCH') {
    return record.dispatchedTime;
  }

  return null;
};

const getDeliveryStatus = (record: JourneyRecord): string | null =>
  (record.hedwigStatusDetails != null
    ? record.hedwigStatusDetails
    : record.hedwigStatusCode);

const getErrorType = (record: JourneyRecord): ErrorType | null => {
  const deliveryStatus = record.hedwigStatusDetails
    ? record.hedwigStatusDetails.toLowerCase()
    : null;
  const statusCode = record.hedwigStatusCode
    ? record.hedwigStatusCode.toLowerCase()
    : null;

  if (record.rfStatus === 'FAILED_RF') {
    return {
      label: ERROR_TYPE_FILTER_TEXT.FAILED_RF,
      color: 'red',
    };
  }

  if (record.xcoStatus === 'DROPPED_FREQ_CAP') {
    return {
      label: ERROR_TYPE_FILTER_TEXT.DROPPED_FREQ_CAP,
      color: 'red',
    };
  }

  if (record.hedwigHTTPCode != null && record.hedwigHTTPCode >= 400) {
    return {
      label: `HTTP ${record.hedwigHTTPCode}`,
      color: record.hedwigHTTPCode >= 500 ? 'red' : 'orange',
    };
  }

  if (
    record.hedwigStatusCode &&
    statusCode &&
    !DELIVERY_SUCCESS_STATUSES.includes(statusCode)
  ) {
    return {
      label: record.hedwigStatusCode,
      color: 'red',
    };
  }

  if (deliveryStatus && !DELIVERY_SUCCESS_STATUSES.includes(deliveryStatus)) {
    return {
      label: record.hedwigStatusDetails || '',
      color: 'red',
    };
  }

  return null;
};

const renderDeliveryStatus = (record: JourneyRecord) => {
  const status = getDeliveryStatus(record);

  if (!status) return missingValue;

  const isSuccess = DELIVERY_SUCCESS_STATUSES.includes(status.toLowerCase());
  return <Tag color={isSuccess ? 'green' : 'red'}>{status}</Tag>;
};

const renderErrorType = (record: JourneyRecord) => {
  const errorType = getErrorType(record);

  if (!errorType) return missingValue;

  return <Tag color={errorType.color}>{errorType.label}</Tag>;
};

const renderCampaign = (record: JourneyRecord) => {
  if (!record.ucdCampaignName) return missingValue;

  return (
    <Tooltip title={record.ucdCampaignName}>
      <Typography.Link
        href={getCampaignUrl(record.trackingID, record.ucdCampaignID)}
        target="_blank"
        rel="noreferrer"
      >
        {record.ucdCampaignName}
      </Typography.Link>
    </Tooltip>
  );
};

const renderContent = (record: JourneyRecord) => {
  if (record.hedwigMessage) {
    return (
      <Tooltip
        title={
          <span style={{ whiteSpace: 'pre-line' }}>
            {record.hedwigMessage}
          </span>
        }
      >
        <Typography.Paragraph
          copyable={{ text: record.hedwigMessage }}
          ellipsis={{ rows: 3 }}
          style={{ marginBottom: 0, whiteSpace: 'pre-line' }}
        >
          {record.hedwigMessage}
        </Typography.Paragraph>
      </Tooltip>
    );
  }

  if (!record.templateID) return missingValue;

  return (
    <Flex vertical gap={4}>
      <Tooltip title={record.templateID}>
        <Typography.Link
          href={getContentUrl(record.templateID)}
          target="_blank"
          rel="noreferrer"
        >
          {record.templateID}
        </Typography.Link>
      </Tooltip>
      {record.modelSource && (
        <Tag color="blue" icon={<DocumentOutlined />}>
          {record.modelSource}
        </Tag>
      )}
    </Flex>
  );
};

const initialSelectedRecord: JourneyRecord | null = null;

const DebuggingToolsPage: React.FC = () => {
  const dispatch = useDispatch();
  const { token } = theme.useToken();
  const [form] = Form.useForm();
  const [selectedRecord, setSelectedRecord] = useState(initialSelectedRecord);
  const journeyResults = useSelector((state: any) =>
    _.get(state, 'debuggingTools.journeyResults', []),
  );
  const hasSearched = useSelector((state: any) =>
    _.get(state, 'debuggingTools.hasSearched', false),
  );
  const searchRequest = useSelector((state: any) =>
    _.get(state.requests, 'searchSmartCommsJourneyRequest', {}),
  );
  const isPending = searchRequest.isPending;

  useEffect(() => {
    if (process.env.NODE_ENV === 'development') {
      const handler = (event: ErrorEvent) => {
        if (event.message === 'ResizeObserver loop completed with undelivered notifications.') {
          event.stopImmediatePropagation();
        }
      };
      window.addEventListener('error', handler);
      return () => window.removeEventListener('error', handler);
    }
  }, []);

  const columns = [
    {
      title: 'User ID',
      dataIndex: 'userID',
      key: 'userID',
      width: 140,
    },
    {
      title: 'Sent Time',
      key: 'actualSentTime',
      width: 220,
      render: (_: unknown, record: JourneyRecord) =>
        formatDate(getActualSentTime(record)),
    },
    {
      title: 'Delivery Status',
      key: 'deliveryStatus',
      width: 160,
      render: (_: unknown, record: JourneyRecord) =>
        renderDeliveryStatus(record),
    },
    {
      title: 'Error Type',
      key: 'errorType',
      width: 150,
      render: (_: unknown, record: JourneyRecord) => renderErrorType(record),
    },
    {
      title: 'Campaign',
      dataIndex: 'ucdCampaignName',
      key: 'ucdCampaignName',
      width: 360,
      ellipsis: true,
      render: (_: unknown, record: JourneyRecord) => renderCampaign(record),
    },
    {
      title: 'Content',
      dataIndex: 'templateID',
      key: 'templateID',
      width: 320,
      ellipsis: true,
      render: (_: unknown, record: JourneyRecord) => renderContent(record),
    },
  ];

  const onSearch = () => {
    setSelectedRecord(null);
    form
      .validateFields()
      .then((values: any) => {
        const { userId, dateRange, campaignId } = values;
        dispatch(
          searchSmartCommsJourney({
            userId,
            startDate: dateRange[0].format('YYYY-MM-DD'),
            endDate: dateRange[1].format('YYYY-MM-DD'),
            campaignId: campaignId || undefined,
          }),
        );
      })
      .catch((error: any) => {
        console.error('Search failed:', error);
      });
  };

  const renderResults = () => {
    if (!hasSearched && !isPending) {
      return <Empty description="No search has run yet." />;
    }

    if (hasSearched && journeyResults.length === 0 && !isPending) {
      return (
        <Empty description="No records found for this user in the selected range." />
      );
    }

    return (
      <Card
        style={{ borderColor: token.colorBorderSecondary }}
        styles={{ body: { padding: 0 } }}
      >
        <Spin spinning={isPending}>
          <Table
            dataSource={journeyResults}
            columns={columns}
            rowKey={(record: JourneyRecord) =>
              record.hedwigMessageID ||
              `${record.userID}-${record.dispatchedTime}-${record.templateID}`
            }
            scroll={{ x: 'max-content' }}
            size="small"
            pagination={{
              pageSize: RESULTS_PAGE_SIZE,
              showSizeChanger: false,
            }}
            onRow={(record: JourneyRecord) => ({
              onClick: () => setSelectedRecord(record),
              style: { cursor: 'pointer' },
            })}
          />
        </Spin>
      </Card>
    );
  };

  return (
    <div
      style={{
        minHeight: '100%',
        padding: token.paddingLG,
        background: token.colorBgContainer,
      }}
    >
      <Flex
        align="center"
        justify="space-between"
        style={{ marginBottom: token.marginLG }}
      >
        <Title level={4} style={{ margin: 0 }}>
          Debugging Tools
        </Title>
        {hasSearched && (
          <Typography.Metadata type="secondary">
            {journeyResults.length} records
          </Typography.Metadata>
        )}
      </Flex>
      <Card
        style={{
          marginBottom: '24px',
          borderColor: colorBaseNeutral75,
          background: '#fafafa',
        }}
        styles={{ body: { padding: '16px 20px' } }}
      >
        <Form form={form} layout="inline" colon={false}>
          <Flex wrap="wrap" align="flex-start" gap={12}>
            <Form.Item
              name="userId"
              rules={[{ required: true, message: 'User ID is required' }]}
              style={{ marginBottom: 0 }}
            >
              <Input
                prefix={<UserOutlined />}
                placeholder="User ID"
                style={{ width: '220px', height: '36px' }}
              />
            </Form.Item>
            <Form.Item
              name="dateRange"
              rules={[{ required: true, message: 'Date range is required' }]}
              style={{ marginBottom: 0 }}
            >
              <RangePicker
                showTime
                format="YYYY-MM-DD HH:mm"
                placeholder={['Start date', 'End date']}
                style={{ height: '36px' }}
              />
            </Form.Item>
            <Form.Item name="campaignId" style={{ marginBottom: 0 }}>
              <Input
                prefix={<TicketOutlined />}
                placeholder="Campaign ID (optional)"
                style={{ width: '220px', height: '36px' }}
              />
            </Form.Item>
            <Form.Item style={{ marginBottom: 0 }}>
              <Button
                type="primary"
                onClick={onSearch}
                loading={isPending}
                icon={<SearchUniversal />}
                style={{ height: '36px' }}
              >
                Search
              </Button>
            </Form.Item>
          </Flex>
        </Form>
      </Card>
      {journeyResults.length >= 1000 && (
        <Alert
          type="warning"
          showIcon
          message="Results are capped at 1000 records - narrow the date range to see more specific data."
          style={{ marginBottom: 16 }}
        />
      )}
      {renderResults()}
      <DebuggingToolsDrawer
        record={selectedRecord}
        open={!!selectedRecord}
        onClose={() => setSelectedRecord(null)}
      />
    </div>
  );
};

export default DebuggingToolsPage;
